import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { keyframes } from '@mui/material/styles';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import GetAppIcon from '@mui/icons-material/GetApp';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { apiFetch } from '../api/api';
import ProductGridItem from '../components/ProductGridItem';
import CategoryCarousel from '../components/CategoryCarousel';
import { isAppServiceItem, withServiceQuery } from '../config/service';
import { mapApiProduct, sortApiProducts } from '../utils/mapProduct';

const HOME_PRODUCTS_DISPLAY_COUNT = 12;
const FEATURED_CATEGORY_ID = '26';
const HOME_EXCLUDED_CATEGORY_IDS = new Set([FEATURED_CATEGORY_ID]);
const QUESTION_WHATSAPP = 'https://wa.me/9647500322000';
const CATEGORIES_FETCH_LIMIT = 150;

const CATEGORY_SKELETON_COUNT = { xs: 9, md: 10 };
// 12 / 3 = 4 products per row
const PRODUCT_GRID = { xs: 6, sm: 3, md: 3, lg: 3 };

function mapCategory(apiCategory) {
  return {
    id: String(apiCategory.id),
    name: apiCategory.name,
    image: apiCategory.image_url || null,
  };
}

function isExcludedFromHomeProducts(apiProduct) {
  const categoryId = String(apiProduct.category_id ?? apiProduct.category?.id ?? '');
  return HOME_EXCLUDED_CATEGORY_IDS.has(categoryId);
}

function pickRandomHomeProducts(items, count) {
  if (items.length <= count) return items;
  const pool = items.slice();
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

function SectionHeader({ title }) {
  return (
    <Box
      sx={{
        mb: { xs: 2.5, md: 3 },
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1.25, md: 1.5 },
      }}
    >
      <Box
        sx={{
          width: 5,
          height: { xs: 28, md: 34 },
          borderRadius: 999,
          flexShrink: 0,
          background: 'linear-gradient(145deg, #1f6e6b 0%, #2a938f 55%, #3ab5b0 100%)',
          boxShadow: '0 2px 10px rgba(42,147,143,0.28)',
        }}
      />
      <Box>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{
            color: 'primary.main',
            fontSize: { xs: '1.35rem', md: '1.7rem' },
            lineHeight: 1.2,
            letterSpacing: 0.2,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            mt: 0.75,
            width: { xs: 40, md: 52 },
            height: 3,
            borderRadius: 999,
            background: 'linear-gradient(145deg, #1f6e6b 0%, #2a938f 55%, #3ab5b0 100%)',
            opacity: 0.85,
          }}
        />
      </Box>
    </Box>
  );
}

function EmptyBlock({ message }) {
  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        py: 5,
        px: 3,
        textAlign: 'center',
        borderRadius: 3,
        bgcolor: 'background.paper',
      }}
    >
      <Typography color="text.secondary" fontWeight={600}>
        {message}
      </Typography>
    </Paper>
  );
}

const promoButtonSx = {
  width: '100%',
  maxWidth: 420,
  mx: 'auto',
  py: { xs: 1.6, sm: 1.85 },
  px: { xs: 2.5, sm: 3.5 },
  borderRadius: 999,
  fontWeight: 800,
  fontSize: { xs: '1rem', sm: '1.1rem' },
  lineHeight: 1.3,
  color: '#fff',
  textTransform: 'none',
  letterSpacing: 0.2,
  background: 'linear-gradient(135deg, #25D366 0%, #1ebe57 45%, #128C7E 100%)',
  boxShadow: '0 8px 24px rgba(37,211,102,0.32)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  gap: 1,
  '&:hover': {
    transform: 'translateY(-2px)',
    background: 'linear-gradient(135deg, #2fe472 0%, #25D366 45%, #1aa382 100%)',
    boxShadow: '0 12px 28px rgba(37,211,102,0.4)',
  },
};

const softFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const shimmerSweep = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(120%); }
`;

function AskQuestionButton({ label }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, md: 4 } }}>
      <Button
        component="a"
        href={QUESTION_WHATSAPP}
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<WhatsAppIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />}
        sx={promoButtonSx}
      >
        {label}
      </Button>
    </Box>
  );
}

function DownloadPromoBanner({ t, onDownload }) {
  const perks = [
    { key: 'gift', label: t('home_download_gift'), Icon: CardGiftcardIcon },
    { key: 'reward', label: t('home_download_reward'), Icon: EmojiEventsIcon },
    { key: 'discount', label: t('home_download_discount'), Icon: LocalOfferIcon },
    { key: 'delivery', label: t('home_download_free_delivery'), Icon: LocalShippingIcon },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        mb: { xs: 4, md: 5 },
        borderRadius: { xs: 4, md: 5 },
        px: { xs: 2.25, sm: 3.5, md: 5 },
        py: { xs: 3, sm: 3.5, md: 4 },
        background:
          'linear-gradient(135deg, #1f6e6b 0%, #2a938f 42%, #3ab5b0 78%, #2a938f 100%)',
        boxShadow: '0 16px 40px rgba(42,147,143,0.28)',
        color: '#fff',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 12% 20%, rgba(255,255,255,0.16), transparent 42%), radial-gradient(circle at 88% 78%, rgba(58,181,176,0.35), transparent 46%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '45%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
          animation: `${shimmerSweep} 5.5s ease-in-out infinite`,
          pointerEvents: 'none',
        }}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2.5, md: 4 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
            <Box
              sx={{
                width: { xs: 42, md: 48 },
                height: { xs: 42, md: 48 },
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                background: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255,255,255,0.22)',
                animation: `${softFloat} 3.2s ease-in-out infinite`,
              }}
            >
              <SmartphoneIcon sx={{ fontSize: { xs: 24, md: 26 }, color: '#fff' }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.35rem', sm: '1.55rem', md: '1.85rem' },
                lineHeight: 1.25,
                letterSpacing: 0.2,
              }}
            >
              {t('home_download_title')}
            </Typography>
          </Stack>

          <Typography
            sx={{
              mb: 2,
              maxWidth: 560,
              color: 'rgba(255,255,255,0.9)',
              fontWeight: 600,
              fontSize: { xs: '0.98rem', md: '1.08rem' },
              lineHeight: 1.6,
            }}
          >
            {t('home_download_subtitle')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {perks.map(({ key, label, Icon }) => (
              <Box
                key={key}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.7,
                  borderRadius: 999,
                  bgcolor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <Icon sx={{ fontSize: 18, color: '#E6F4F3' }} />
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: '0.82rem', sm: '0.9rem' },
                    fontWeight: 700,
                    color: '#fff',
                    lineHeight: 1.2,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Button
          onClick={onDownload}
          variant="contained"
          startIcon={<GetAppIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />}
          sx={{
            flexShrink: 0,
            alignSelf: { xs: 'stretch', md: 'center' },
            width: { xs: '100%', md: 'auto' },
            minWidth: { md: 220 },
            py: { xs: 1.6, sm: 1.85 },
            px: { xs: 2.5, sm: 3.5 },
            borderRadius: 999,
            fontWeight: 800,
            fontSize: { xs: '1rem', sm: '1.1rem' },
            lineHeight: 1.3,
            color: '#1f6e6b',
            textTransform: 'none',
            letterSpacing: 0.2,
            gap: 1,
            background: '#fff',
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
            border: '1px solid rgba(255,255,255,0.35)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '& .MuiButton-startIcon': {
              marginInlineEnd: 0.5,
              marginInlineStart: 0,
            },
            '&:hover': {
              transform: 'translateY(-2px)',
              background: '#F4FAF9',
              boxShadow: '0 12px 28px rgba(0,0,0,0.2)',
            },
          }}
        >
          {t('home_download_cta')}
        </Button>
      </Stack>
    </Box>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [vipProducts, setVipProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const homeProductsPoolRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    setCategoriesLoading(true);
    (async () => {
      try {
        const response = await apiFetch(
          withServiceQuery(`/users/categories?per_page=${CATEGORIES_FETCH_LIMIT}`),
        );
        if (!response.ok) throw new Error('categories_fetch_failed');
        const json = await response.json();
        const items = (json.data ?? [])
          .filter(isAppServiceItem)
          .slice()
          .sort((a, b) => Number(a.sort) - Number(b.sort))
          .map(mapCategory);
        if (!cancelled) setCategories(items);
      } catch {
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language]);

  useEffect(() => {
    let cancelled = false;
    setProductsLoading(true);
    (async () => {
      try {
        const rawItems = [];
        let page = 1;
        let lastPage = 1;
        do {
          const response = await apiFetch(withServiceQuery(`/users/products?page=${page}`));
          if (!response.ok) throw new Error('products_fetch_failed');
          const json = await response.json();
          rawItems.push(...(json.data ?? []).filter(isAppServiceItem));
          lastPage = json.meta?.last_page ?? 1;
          page += 1;
        } while (page <= lastPage);

        const pool = sortApiProducts(rawItems)
          .filter((item) => !isExcludedFromHomeProducts(item))
          .map(mapApiProduct);
        homeProductsPoolRef.current = pool;
        if (!cancelled) {
          const vip = pool.filter((item) => item.isVip);
          const regular = pool.filter((item) => !item.isVip);
          setVipProducts(vip);
          setProducts(pickRandomHomeProducts(regular, HOME_PRODUCTS_DISPLAY_COUNT));
        }
      } catch {
        if (!cancelled) {
          homeProductsPoolRef.current = [];
          setVipProducts([]);
          setProducts([]);
        }
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language]);

  const openCategory = useCallback(
    (category) => {
      navigate(`/category/${category.id}`, { state: { categoryName: category.name } });
    },
    [navigate],
  );

  const openDownloadPage = useCallback(() => {
    navigate('/download');
  }, [navigate]);

  return (
    <Box sx={{ bgcolor: 'background.default', pb: { xs: 2, md: 4 } }}>
      <Box
        component="picture"
        sx={{ display: 'block', width: '100%', lineHeight: 0 }}
      >
        <source media="(min-width: 900px)" srcSet="/images/coverImageToPc.png" />
        <Box
          component="img"
          src="/images/coverImageToMobile.png"
          alt={t('app_name')}
          loading="eager"
          decoding="async"
          sx={{
            width: '100%',
            maxWidth: { md: 1935 },
            mx: { md: 'auto' },
            display: 'block',
            objectFit: 'cover',
            objectPosition: 'center',
            height: { xs: 180, sm: 220, md: 'auto' },
            aspectRatio: { md: '1935 / 813' },
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        {categoriesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, md: 4 } }}>
            <Skeleton variant="rounded" height={52} sx={{ width: '100%', maxWidth: 420, borderRadius: 999 }} />
          </Box>
        ) : (
          <AskQuestionButton label={t('medical_consultation')} />
        )}

        <DownloadPromoBanner t={t} onDownload={openDownloadPage} />

        <Box sx={{ mb: { xs: 4, md: 5 } }}>
          <SectionHeader title={t('categories')} />

          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: { xs: 1.5, sm: 2, md: 2.5 },
              borderRadius: 4,
              bgcolor: 'background.paper',
              boxShadow: '0 4px 24px rgba(14, 227, 234, 0.06)',
            }}
          >
            {categoriesLoading ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(3, minmax(0, 1fr))',
                    md: 'repeat(5, minmax(0, 1fr))',
                  },
                  gridTemplateRows: {
                    xs: 'repeat(3, auto)',
                    md: 'repeat(2, auto)',
                  },
                  gap: { xs: 1.5, sm: 2, md: 2.5 },
                }}
              >
                {Array.from({ length: CATEGORY_SKELETON_COUNT.md }).map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={130}
                    sx={{
                      borderRadius: 3,
                      display: { xs: i < CATEGORY_SKELETON_COUNT.xs ? 'block' : 'none', md: 'block' },
                    }}
                  />
                ))}
              </Box>
            ) : categories.length === 0 ? (
              <EmptyBlock message={t('products_empty')} />
            ) : (
              <CategoryCarousel
                categories={categories}
                onCategoryClick={openCategory}
                noImageLabel={t('no_image')}
              />
            )}
          </Paper>
        </Box>

        {vipProducts.length > 0 && (
          <Box sx={{ mb: { xs: 4, md: 5 } }}>
            <SectionHeader title={t('vip_products')} />

            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
              {vipProducts.map((product) => (
                <Grid key={product.id} size={PRODUCT_GRID}>
                  <ProductGridItem
                    item={product}
                    addToCartLabel={t('add_to_cart')}
                    favouriteLabel={t('favourite_a11y')}
                    discountOffLabel={t('discount_off')}
                    unavailableLabel={t('product_unavailable')}
                    vipLabel={t('vip_badge')}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box>
          <SectionHeader title={t('products')} />

          {productsLoading ? (
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Grid key={i} size={PRODUCT_GRID}>
                  <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          ) : products.length === 0 ? (
            <EmptyBlock message={t('products_empty')} />
          ) : (
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
              {products.map((product) => (
                <Grid key={product.id} size={PRODUCT_GRID}>
                  <ProductGridItem
                    item={product}
                    addToCartLabel={t('add_to_cart')}
                    favouriteLabel={t('favourite_a11y')}
                    discountOffLabel={t('discount_off')}
                    unavailableLabel={t('product_unavailable')}
                    vipLabel={t('vip_badge')}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Container>
    </Box>
  );
}
